import React, { useState } from 'react';
import { Send, Instagram, MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    vehicleType: '',
    date: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - will be replaced with real backend
    console.log('Form submitted (MOCK):', formData);
    
    // Show success state
    setIsSubmitted(true);
    toast.success('Demande envoyée avec succès ! Je vous recontacterai rapidement.');
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        city: '',
        vehicleType: '',
        date: '',
        message: ''
      });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section
      id="contact"
      className="section-spacing-large"
      style={{ backgroundColor: 'var(--color-black)' }}
    >
      <div className="container-artworld">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p className="label-text mb-4">Contactez-nous</p>
          <h2 className="section-title">Réservez Votre Shooting</h2>
          <p className="body-text max-w-3xl mx-auto mt-6">
            Prêt à immortaliser votre véhicule ? Remplissez le formulaire ci-dessous 
            et je vous recontacterai rapidement pour organiser votre séance photo.
          </p>
          <div
            className="w-24 h-1 mx-auto mt-6"
            style={{
              background: 'linear-gradient(90deg, transparent, var(--color-red-accent), transparent)'
            }}
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h3
              className="subtitle mb-8"
              style={{ fontSize: '1.5rem', color: 'var(--color-white)' }}
            >
              Informations de Contact
            </h3>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div
                  className="p-3 rounded-full flex-shrink-0"
                  style={{
                    background: 'rgba(220, 38, 38, 0.1)',
                    border: '1px solid var(--color-red-accent)'
                  }}
                >
                  <MapPin size={24} style={{ color: 'var(--color-red-accent)' }} />
                </div>
                <div>
                  <h4 className="label-text mb-2" style={{ color: 'var(--color-white)' }}>
                    Localisation
                  </h4>
                  <p className="body-text">Bretagne, France</p>
                  <p className="caption-text">Shootings en France & Monaco</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="p-3 rounded-full flex-shrink-0"
                  style={{
                    background: 'rgba(220, 38, 38, 0.1)',
                    border: '1px solid var(--color-red-accent)'
                  }}
                >
                  <Instagram size={24} style={{ color: 'var(--color-red-accent)' }} />
                </div>
                <div>
                  <h4 className="label-text mb-2" style={{ color: 'var(--color-white)' }}>
                    Instagram
                  </h4>
                  <a
                    href="https://www.instagram.com/w_shoot_22/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="body-text hover:text-white transition-colors duration-300"
                  >
                    @w_shoot_22
                  </a>
                  <p className="caption-text">Suivez notre actualité</p>
                </div>
              </div>
            </div>

            {/* Instagram CTA */}
            <a
              href="https://www.instagram.com/w_shoot_22/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <Instagram size={20} />
              <span>Suivre sur Instagram</span>
            </a>
          </div>

          {/* Contact Form */}
          <div
            className="p-8"
            style={{
              background: 'var(--color-carbon)',
              border: '1px solid var(--color-dark-gray)'
            }}
          >
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '2px solid #22c55e'
                  }}
                >
                  <Check size={40} style={{ color: '#22c55e' }} />
                </div>
                <h3
                  className="subtitle mb-4"
                  style={{ fontSize: '1.5rem', color: 'var(--color-white)' }}
                >
                  Demande Envoyée !
                </h3>
                <p className="body-text">
                  Merci pour votre demande. Je vous recontacterai très rapidement.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="label-text block mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 body-text"
                    style={{
                      background: 'var(--color-black)',
                      border: '1px solid var(--color-dark-gray)',
                      color: 'var(--color-white)',
                      outline: 'none',
                      transition: 'border-color var(--transition-fast)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-red-accent)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-dark-gray)';
                    }}
                  />
                </div>

                {/* City */}
                <div>
                  <label htmlFor="city" className="label-text block mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 body-text"
                    style={{
                      background: 'var(--color-black)',
                      border: '1px solid var(--color-dark-gray)',
                      color: 'var(--color-white)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-red-accent)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-dark-gray)';
                    }}
                  />
                </div>

                {/* Vehicle Type */}
                <div>
                  <label htmlFor="vehicleType" className="label-text block mb-2">
                    Type de Véhicule *
                  </label>
                  <input
                    type="text"
                    id="vehicleType"
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Ferrari 488, BMW M3, Kawasaki Z900..."
                    className="w-full px-4 py-3 body-text"
                    style={{
                      background: 'var(--color-black)',
                      border: '1px solid var(--color-dark-gray)',
                      color: 'var(--color-white)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-red-accent)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-dark-gray)';
                    }}
                  />
                </div>

                {/* Date */}
                <div>
                  <label htmlFor="date" className="label-text block mb-2">
                    Date Souhaitée
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 body-text"
                    style={{
                      background: 'var(--color-black)',
                      border: '1px solid var(--color-dark-gray)',
                      color: 'var(--color-white)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-red-accent)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-dark-gray)';
                    }}
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="label-text block mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Décrivez votre projet, vos attentes, le lieu souhaité..."
                    className="w-full px-4 py-3 body-text resize-none"
                    style={{
                      background: 'var(--color-black)',
                      border: '1px solid var(--color-dark-gray)',
                      color: 'var(--color-white)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--color-red-accent)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--color-dark-gray)';
                    }}
                  />
                </div>

                {/* Submit Button */}
                <button type="submit" className="btn-accent w-full flex items-center justify-center gap-2">
                  <Send size={20} />
                  <span>Envoyer la Demande</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
